import { movies, type Movie } from '@/mocks/movies';

export type CategorySlug = 'movie' | 'series' | 'short';

export const categoryMeta: Record<CategorySlug, { slug: CategorySlug; label: string; en: string; desc: string; icon: string }> = {
  movie: {
    slug: 'movie',
    label: '电影',
    en: 'Movies',
    desc: '从悬疑犯罪到科幻史诗，精选全球高分电影，一屏览尽银幕精彩。',
    icon: 'ri-film-line',
  },
  series: {
    slug: 'series',
    label: '电视剧',
    en: 'TV Series',
    desc: '都市情感、历史传奇、悬疑推理，值得追的剧集都在这里。',
    icon: 'ri-tv-2-line',
  },
  short: {
    slug: 'short',
    label: '短视频',
    en: 'Shorts',
    desc: '几分钟一个故事，碎片时间也能沉浸其中的短剧与短片。',
    icon: 'ri-play-circle-line',
  },
};

export const regionOptions = ['全部', '大陆', '港台', '欧美', '日本', '其他'];

export const sortOptions: { value: SortKey; label: string }[] = [
  { value: 'latest', label: '最新' },
  { value: 'hot', label: '热门' },
  { value: 'rating', label: '评分' },
];

export type SortKey = 'latest' | 'hot' | 'rating';

export interface FilterState {
  region: string;
  year: string;
  sort: SortKey;
}

export const defaultFilter: FilterState = { region: '全部', year: '全部', sort: 'latest' };

export const yearOptions = ['全部', '2024', '2023', '2022', '2021', '2020 及更早'];

export function getMovieById(id: string): Movie | undefined {
  return movies.find((m) => m.id === id);
}

export function getByType(type: CategorySlug): Movie[] {
  return movies.filter((m) => m.type === type);
}

export function getFeatured(): Movie[] {
  const featured = movies.filter((m) => m.featured);
  return featured.length > 0 ? featured : movies.slice(0, 5);
}

export function getLatest(count = 12): Movie[] {
  return [...movies].sort((a, b) => b.year - a.year).slice(0, count);
}

export function getPopular(count = 8): Movie[] {
  return [...movies].sort((a, b) => b.rating - a.rating).slice(0, count);
}

export function getRelated(movie: Movie, count = 5): Movie[] {
  const sameType = movies.filter((m) => m.id !== movie.id && m.type === movie.type);
  const sameGenre = movies.filter(
    (m) => m.id !== movie.id && m.type !== movie.type && m.genres.some((g) => movie.genres.includes(g)),
  );
  return [...sameType, ...sameGenre].slice(0, count);
}

export function searchMovies(query: string): Movie[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return movies.filter(
    (m) =>
      m.title.toLowerCase().includes(q) ||
      m.genres.some((g) => g.toLowerCase().includes(q)) ||
      m.director.toLowerCase().includes(q) ||
      m.cast.some((c) => c.toLowerCase().includes(q)),
  );
}

function yearMatches(year: number, range: string): boolean {
  if (range === '全部') return true;
  if (range === '2020 及更早') return year <= 2020;
  return String(year) === range;
}

export function applyFilter(list: Movie[], filter: FilterState): Movie[] {
  const filtered = list.filter((m) => {
    const regionOk = filter.region === '全部' || m.region === filter.region;
    const yearOk = yearMatches(m.year, filter.year);
    return regionOk && yearOk;
  });

  const sorted = [...filtered];
  if (filter.sort === 'latest') sorted.sort((a, b) => b.year - a.year || b.rating - a.rating);
  else if (filter.sort === 'rating') sorted.sort((a, b) => b.rating - a.rating);
  else sorted.sort((a, b) => b.rating * b.year - a.rating * a.year);
  return sorted;
}

export const PAGE_SIZE = 20;